import React, { useState, useEffect } from 'react';
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { toast } from 'react-hot-toast';
import { Slider } from "../../components/ui/slider";
import { Check, AlertTriangle, Award, Star } from 'lucide-react';
import { fetchWithAuth } from '../../utils/api';
import RatingEventBus from '../../services/RatingEventBus';

interface PanelistViewProps {
  roundData: any;
  roundId: string;
}

const PanelistView: React.FC<PanelistViewProps> = ({ roundData, roundId }) => {
  const [adjudicatorRatings, setAdjudicatorRatings] = useState<{ [key: number]: number }>({});
  const [submitting, setSubmitting] = useState(false);
  const [ratingsModified, setRatingsModified] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Add component ID for tracking
  const componentId = `panelist-view-${roundId}`;

  // Register the component on mount
  useEffect(() => {
    RatingEventBus.registerComponent(componentId);

    return () => {
      RatingEventBus.unregisterComponent(componentId);
    };
  }, [componentId]);

  // Check submission status on component mount
  useEffect(() => {
    const checkSubmissionStatus = async () => {
      try {
        const response = await fetchWithAuth(`/api/round/${roundId}/rating-status/`);

        // Find the current user's submission status
        if (response && response.participants && response.participants.adjudicators) {
          const currentUser = response.participants.adjudicators.find(
            (adj: any) => adj.user_id === roundData.current_user_id
          );

          if (currentUser && currentUser.has_submitted_ratings) {
            setHasSubmitted(true);
          }
        }
      } catch (err) {
        console.error("Error checking submission status:", err);
      }
    };

    // Check if already submitted from sessionStorage (faster)
    const savedSubmissionState = sessionStorage.getItem(`panelist-${roundId}-submitted`);

    if (savedSubmissionState === 'true') {
      setHasSubmitted(true);
    } else {
      // Only check submission status if not already marked as submitted
      checkSubmissionStatus();
    }
  }, [roundId, roundData.current_user_id]);

  // Get chair adjudicator that can be rated
  const getChairAdjudicator = () => {
    return roundData.participants.filter((p: any) =>
      p.role === 'Chair Adjudicator'
    );
  };

  // Handle rating change
  const handleRatingChange = (adjudicatorId: number, value: number[]) => {
    setAdjudicatorRatings({
      ...adjudicatorRatings,
      [adjudicatorId]: value[0]
    });
    setRatingsModified(true);
  };

  // Update submit function to ensure proper payload structure

const submitRatings = async () => {
  try {
    setSubmitting(true);
    
    // Create ratings array from the state
    const ratings = Object.entries(adjudicatorRatings).map(([adjudicatorId, rating]) => {
      // Get the actual adjudicator object from the round data
      const adjudicator = getChairAdjudicator().find((a: any) => a.id === parseInt(adjudicatorId));
      return {
        adjudicator_id: adjudicator?.user?.id, // Use the actual user ID, not the allocation ID
        rating: rating
      };
    }).filter(item => item.adjudicator_id); // Filter out any undefined adjudicator_ids
    
    if (ratings.length === 0) {
      toast.error('Please provide a rating for the Chair Adjudicator');
      setSubmitting(false);
      return;
    }
    
    // Mark submission in EventBus
    RatingEventBus.markUserSubmitted(
      roundId, 
      roundData.current_user_id, 
      roundData.current_username, 
      'Panelist'
    );
    
    // Store submission state
    sessionStorage.setItem(`panelist-${roundId}-submitted`, 'true');
    setHasSubmitted(true);
    
    // Submit to new endpoint
    console.log(`Submitting Chair Adjudicator rating:`, ratings);
    const response = await fetchWithAuth(`/api/round/${roundId}/submit-adjudicator-ratings/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ ratings })
    });
    
    toast.success('Rating submitted successfully');
    setRatingsModified(false);
    
    // Request manual refresh
    RatingEventBus.publish('MANUAL_REFRESH_REQUESTED', { 
      roundId, 
      timestamp: new Date().toISOString() 
    });
    
  } catch (error) {
    console.error("Error submitting rating:", error);
    toast.error(`Error submitting rating: ${error}`);
  } finally {
    setSubmitting(false);
  }
};

  const renderMotion = (motion: any) => {
    if (!motion) return 'No motion available';

    // If motion is an object with a text property
    if (typeof motion === 'object' && motion !== null) {
      return motion.text || 'No motion text available';
    }

    // If motion is a string
    return motion;
  };

  return (
    <div className="space-y-6">
      {/* User role info */}
      <div className="p-4 border-2 border-purple-500 bg-purple-900 bg-opacity-10 rounded-lg">
        <h3 className="font-semibold mb-2">Panelist</h3>
        <p className="text-sm">
          As a Panelist, you can rate the Chair Adjudicator's performance.
        </p>
      </div>

      {/* Display either the rating interface or submission confirmation */}
      <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <h3 className="font-semibold mb-4 flex items-center">
          <Award className="mr-2" size={18} />
          Rate Chair Adjudicator
        </h3>

        <div className="space-y-4">
          {!hasSubmitted ? (
            <>
              {getChairAdjudicator().length > 0 ? (
                <>
                  {getChairAdjudicator().map((adj: any) => (
                    <Card key={adj.id} className="p-3 border">
                      {/* Use CardContent here to fix the unused import */}
                      <CardContent>
                        <div className="flex flex-col md:flex-row md:items-center gap-4">
                          <div className="flex-1">
                            <p className="font-medium">{adj.user.username}</p>
                            <p className="text-xs text-gray-400">{adj.role}</p>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Star className="text-amber-500" size={16} />
                              <p className="text-sm">Rating: {adjudicatorRatings[adj.id] || 1}</p>
                            </div>
                            <Slider
                              defaultValue={[1]}
                              min={1}
                              max={10}
                              step={1}
                              onValueChange={(value) => handleRatingChange(adj.id, value)}
                            />
                            <div className="flex justify-between mt-1">
                              <span className="text-xs text-gray-400">Poor (1)</span>
                              <span className="text-xs text-gray-400">Excellent (10)</span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  <Button
                    onClick={submitRatings}
                    disabled={submitting || Object.keys(adjudicatorRatings).length === 0}
                    className="mt-4"
                  >
                    {submitting ? 'Submitting...' : 'Submit Rating'}
                  </Button>

                  {ratingsModified && (
                    <div className="mt-2 text-amber-500 text-sm">
                      You have unsaved ratings. Please submit to save your changes.
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-500">No Chair Adjudicator available for rating.</p>
              )}
            </>
          ) : (
            <div className="text-center p-4 bg-green-50 dark:bg-green-900 dark:bg-opacity-20 rounded-lg">
              <Check size={32} className="mx-auto mb-2 text-green-500" />
              <h4 className="font-medium text-green-700 dark:text-green-400">Rating Submitted</h4>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Your rating for the Chair Adjudicator has been successfully submitted.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Information about the round */}
      <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <h3 className="text-lg font-medium mb-4">Round Information</h3>
        <div className="space-y-2">
          <p><span className="font-medium">Motion:</span> {renderMotion(roundData.motion)}</p>
          <p><span className="font-medium">Format:</span> {roundData.format === 'BP' ? 'British Parliamentary' : 'Asian Parliamentary'}</p>
          {roundData.info_slide && (
            <div>
              <p className="font-medium">Info Slide:</p>
              <p className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg mt-1 text-sm">
                {typeof roundData.info_slide === 'object'
                  ? roundData.info_slide.text
                  : roundData.info_slide}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Warning if the user hasn't submitted yet */}
      {!hasSubmitted && (
        <div className="p-4 bg-yellow-500 bg-opacity-10 border border-yellow-500 rounded-lg">
          <div className="flex items-center">
            <AlertTriangle size={18} className="text-yellow-500 mr-2" />
            <p className="text-sm">Please submit your rating for the Chair Adjudicator before the round ends.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PanelistView;